import styles from './PaginatedAuthorsList.module.scss'
import Section from 'components/Section'
import { MediaImageStandard, Typography } from 'components/Contentful'
import { useMemoizedContentGetter } from 'utils/contentful'
import { Box, Pagination } from '@achieve/ascend'
import { AUTHOR_LIMIT_DEFAULT } from 'services/contentful/constants'
import { useRouter } from 'next/router'
import { TitleDivider } from 'components/TitleDivider'
import { AchieveLink } from 'components/AchieveLink'
import { ChevronRight } from 'react-feather'
import { getSlug } from 'utils/conversions'
import { Eyebrow } from 'components/Eyebrow'
import { usePathname, useSearchParams } from 'next/navigation'
import { mainProductConfig } from 'services/contentful/helpers'
import { useContext } from 'react'
import { LayoutContext } from 'providers/LayoutProvider'
import classNames from 'classnames'

const PaginatedAuthorsList = ({ content }) => {
  const { title, listAuthors } = useMemoizedContentGetter(content, ['title', 'listAuthors'])
  const { replace } = useRouter()
  const { state: { siteEnv } = {} } = useContext(LayoutContext)

  // Get the current URL search params, pathname, and page number with the useSearchParams and usePathname hooks
  const urlParams = useSearchParams()
  const pathName = usePathname()
  const currentPage = Number(urlParams.get('page')) || 1

  // On pagination event, update the URL with the new page number from the Pagination component
  // And then replace the URL with the new page number and fetch for new authors data via server side
  const handleChangePage = (_, page) => {
    const params = new URLSearchParams(urlParams)
    if (page) params.set('page', page)
    replace(`${pathName}?${params.toString()}`)
  }

  return (
    <Section className={styles['authors-list-section']}>
      <Box className={styles['authors-list-container']}>
        <TitleDivider
          className={styles['title-divider']}
          title={title}
          titleTag={title?.component}
          data-testid="author-list-title"
        />
        {listAuthors.authorsList.map((author, index) => {
          // Get the author's slug or generate one from the author's name if it doesn't exist
          const authorPageRoute =
            pathName + '/' + (author?.fields?.slug || getSlug(author?.fields?.authorName))
          const { authorName, authorByline, authorPhoto, contentRole } = author?.fields || {}

          {
            return (
              <Box className={styles['author-card']} key={index} data-testid="author-card">
                <Box
                  className={styles['author-photo-container']}
                  data-testid="author-photo-container"
                >
                  {authorPhoto && (
                    <AchieveLink
                      track={{
                        list_name: 'AUTHOR PROFILE IMAGE',
                        click_position: index,
                        click_id: `Photo ${authorPhoto?.fields?.title}`,
                        click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']} | Photo ${authorPhoto?.fields?.title}`,
                      }}
                      data-testid="author-photo-link"
                      href={authorPageRoute}
                    >
                      <span className={styles['img-container']}>
                        <MediaImageStandard
                          className={styles['author-photo']}
                          content={authorPhoto}
                          objectFit="cover"
                          objectPosition="center top"
                          layout="responsive"
                          alt={'Profile Photo of ' + authorName}
                          data-testid="author-photo"
                        />
                      </span>
                    </AchieveLink>
                  )}
                </Box>
                <Box className={styles['author-info']}>
                  <Eyebrow
                    content={contentRole?.join(', ')}
                    className={styles['author-role']}
                    fontWeight="bold"
                    data-testid="author-role-text"
                  />
                  <AchieveLink
                    track={{
                      list_name: 'AUTHOR PROFILE NAME',
                      click_position: index,
                      click_id: authorName,
                      click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']} | ${authorName}`,
                    }}
                    href={authorPageRoute}
                    data-testid="author-name-link"
                  >
                    <Typography
                      className={classNames(styles['author-name'], 'bodyLg lg:headingMd')}
                      content={authorName}
                      fontWeight="medium"
                      data-testid="author-name-text"
                      component="h2"
                    />
                  </AchieveLink>
                  <Typography
                    content={authorByline}
                    variant="bodySm"
                    fontWeight="regular"
                    data-testid="author-byline"
                  />
                  <AchieveLink
                    track={{
                      list_name: 'AUTHOR VIEW PROFILE LINK',
                      click_position: index,
                      click_id: 'View Profile',
                      click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']} | View Profile`,
                    }}
                    href={authorPageRoute}
                    data-testid="author-view-profile-link"
                  >
                    <Box className={styles['author-link']}>
                      <Typography
                        content="View Profile"
                        variant="bodySm"
                        fontWeight="bold"
                        data-testid="author-view-profile-text"
                      />
                      <ChevronRight size={20} />
                    </Box>
                  </AchieveLink>
                </Box>
              </Box>
            )
          }
        })}
        <AchieveLink
          noLink
          track={{
            list_name: 'Pagination Event',
            click_id: `${mainProductConfig[siteEnv || 'achieve']?.['MainProduct']} | Pagination`,
            click_position: 'Pagination',
            click_text: 'Pagination',
            nav_link_section: 'Pagination',
            track_event: 'ui_click',
            event_action: 'ui_click',
            event_type: 'ui_click',
          }}
        >
          <Pagination
            className={styles['author-list-pagination-controls']}
            color="primary"
            count={Math.ceil(parseInt(listAuthors.total) / parseInt(AUTHOR_LIMIT_DEFAULT))}
            onChange={handleChangePage}
            page={currentPage}
            data-testid="author-list-pagination-controls"
          />
        </AchieveLink>
      </Box>
    </Section>
  )
}
export default PaginatedAuthorsList
